/**
 * Bridges the data-import pipeline to the existing dashboard tile renderer.
 *
 * A `WidgetSpec` (produced by a preset or the generic mapper) is turned into an in-memory
 * `TileDefinition` so `DataVisualizationComponent` can draw it with no backend query — see
 * `WidgetRenderer` and the inline-data contract ([[tile-renderer-inline-data]]): the spec's `data`
 * becomes `customData` and its `settings` become `settingsOverride`. The very same spec is later
 * persisted as a real `CustomTile` (see `persistWidgets`), so a previewed widget and a saved one render
 * from identical config.
 */
import { TileDefinition } from '../../types/dashboard';
import { TileSettings } from '../../types/tileBuilderTypes';
import { WidgetSpec } from './presets/types';

/**
 * Build a throwaway `TileDefinition` for rendering `spec` inline. The `id` is negative so it can never
 * collide with a persisted tile id, and `queries`/`transformations` are empty because the data is fed
 * directly via `customData` rather than fetched.
 */
export function widgetSpecToTileDefinition(spec: WidgetSpec, index = 0): TileDefinition {
  const syntheticId = -1 - index;
  return {
    id: syntheticId,
    name: spec.title,
    chart_type: spec.chartType,
    queries: [],
    transformations: [],
    settings: (spec.settings || {}) as TileSettings,
    category: null,
    layout: { i: String(syntheticId), x: 0, y: 0, w: 6, h: 6 },
  } as unknown as TileDefinition;
}
